import type { Agent, StartOptions } from "@fingerprint/agent"
import axiosInstance from "@/api/axios"
import getFingerprintProPublicKey from "@/services/getFingerprintProPublicKey"
import type { FingerprintProPublicKeyData } from "@/types/account"
import fingerprintProTracker from "@/services/fingerprintProTracker"
import fingerprintProEnterpriseTracker from "@/services/fingerprintProEnterpriseTracker"
import { safeLocalStorage } from "@/utils/safeStorage"

// Two independent Fingerprint Pro flows run on the same login (SACRIC-1434):
//   Trial      — trialPublicKey, our own backend proxy, fingerprintProTracker
//   Enterprise — enterprisePublicKey, the infra Cloudflare proxy,
//                fingerprintProEnterpriseTracker, recorded once per account
// getFingerprintProPublicKey decides per login which of the two run. Neither
// flow may affect the other: each one catches its own failures, and they
// never share an agent.

type FingerprintProRegion = NonNullable<StartOptions["region"]>

const DEFAULT_FINGERPRINT_PRO_REGION: FingerprintProRegion = "ap"

const isFingerprintProRegion = (value: string): value is FingerprintProRegion =>
  value === "us" || value === "eu" || value === "ap"

const resolveRegion = (): FingerprintProRegion => {
  const envRegion = import.meta.env.VITE_FINGERPRINT_PRO_REGION
  return envRegion && isFingerprintProRegion(envRegion)
    ? envRegion
    : DEFAULT_FINGERPRINT_PRO_REGION
}

// Both proxies exist so ad blockers that block *.fpjs.io can't break
// collection. A proxy only authenticates public keys of the Fingerprint
// workspace its proxy secret belongs to, so the two flows can't swap proxies.
//
// Trial backend proxy root (SACRIC-1262). The v4 agent derives both URLs from it:
//   agent script  GET  {root}/web/v4/{apiKey}
//   identification POST {root}/...
// The backend forwards sub-paths verbatim to the Fingerprint hosts configured
// in systemsetting (FingerprintProApiBaseUrl, which also serves the agent
// script under /web/v4 in v4). Passing a plain string (not withoutDefault)
// keeps the official Fingerprint endpoints as an automatic fallback if the
// proxy is unreachable — retained as a resilience safety net for Trial.
const TRIAL_BACKEND_PROXY_PATH = "/exchange/member/playerService/fpProxy/ingest"

// The Trial backend proxy lives on the API host that owns the login session,
// so reuse the axios baseURL — the same resolution as every other API call
// (see handleBaseURL in api/axios): "/api" through the Vite proxy in local
// dev, and the API subdomain (VITE_BASE_API_SUBDOMAIN / VITE_BASE_API_URL)
// when deployed.
const resolveTrialBackendProxyEndpoint = (): string => {
  const apiBaseURL = (axiosInstance.defaults.baseURL ?? "").replace(/\/$/, "")
  return `${apiBaseURL}${TRIAL_BACKEND_PROXY_PATH}`
}

// The loader derives the agent script URL from this base as
// {base}/web/v4/{apiKey} and the identification request goes to the base
// itself; ?region tells the proxy which Fingerprint ingress region to forward
// to, and must match the region of the workspace the public key belongs to
// (a mismatch is rejected with wrong_region). Note the loader overwrites the
// query when it builds the script URL, so region reaches the proxy only on the
// identification and helper requests, never on the script download.
//
// Pinned to the single Cloudflare proxy infra provisioned per environment
// (UAT and SIM each have their own host and integration path): every white
// label points at the same host rather than a `metrics.<own domain>` of its
// own. The integration path is the path that proxy is mounted on and is part
// of the base, so both the script URL and the ingress URL inherit it. The
// Enterprise public key has to belong to the workspace this proxy is
// registered with.
//
// Host and path come from VITE_FINGERPRINT_PRO_ENTERPRISE_CLOUDFLARE_PROXY_HOST
// / _PATH; a build missing either one has no Enterprise proxy, so this returns
// undefined and the Enterprise flow does not run there. dev and dev-nginx
// borrow the UAT staging proxy.
const resolveEnterpriseCloudflareProxyEndpoint = (): string | undefined => {
  const proxyHost: string | undefined = import.meta.env
    .VITE_FINGERPRINT_PRO_ENTERPRISE_CLOUDFLARE_PROXY_HOST
  const integrationPath: string | undefined = import.meta.env
    .VITE_FINGERPRINT_PRO_ENTERPRISE_CLOUDFLARE_PROXY_PATH
  if (!proxyHost || !integrationPath) return undefined

  return `${window.location.protocol}//${proxyHost}/${integrationPath}/?region=${resolveRegion()}`
}

// Identification timeout in ms (SDK default 10_000). Raised to 20s because the
// flow is fire-and-forget (App.vue / useFetchLogin only `.catch`), so users
// never wait on it, while our main user base on weak mobile networks needs a
// longer budget — Fingerprint warns that low timeouts increase identification
// failures on slow connections. Covers only the identification round-trip
// (client_timeout), NOT the agent script load (script_load_fail).
const FINGERPRINT_PRO_GET_TIMEOUT_MS = 20000

const TRIAL_TAG = { data: "test" }
// The backend stores tags.data and treats a first_record event with a
// visitorId as "this account is done" (SACRIC-1434).
const ENTERPRISE_TAG = { data: "first_record" }

// Both trackers cap errorMsg at 500 characters.
const TRACKER_ERROR_MSG_MAX_LENGTH = 500
const UNKNOWN_AGENT_ERROR_MESSAGE = "Unknown Fingerprint agent error"

// Enterprise collects once per account, and the frontend owns the isExisted
// flag sent to getFingerprintProPublicKey. It is set only after a successful
// Enterprise collection was accepted by the tracker, so failures (ad
// blockers, network, vendor errors) retry on the next login. Missing storage
// (private mode, sandboxed iframe) or an unknown account just sends false and
// leaves the decision to the backend record table. Bump the version to make
// every account report again if the collection policy changes.
const ENTERPRISE_RECORDED_STORAGE_KEY_PREFIX = "fingerprintProEnterpriseRecorded:v1:"
const ENTERPRISE_RECORDED_STORAGE_VALUE = "1"

const buildEnterpriseRecordedStorageKey = (account: string) =>
  `${ENTERPRISE_RECORDED_STORAGE_KEY_PREFIX}${account}`

const isEnterpriseRecorded = (account: string | undefined): boolean =>
  !!account &&
  safeLocalStorage.getItem(buildEnterpriseRecordedStorageKey(account)) ===
    ENTERPRISE_RECORDED_STORAGE_VALUE

const markEnterpriseRecorded = (account: string | undefined) => {
  if (!account) return
  safeLocalStorage.setItem(
    buildEnterpriseRecordedStorageKey(account),
    ENTERPRISE_RECORDED_STORAGE_VALUE
  )
}

const resolvePublicKeyData = async (
  isExisted: boolean
): Promise<FingerprintProPublicKeyData | undefined> => {
  try {
    return await getFingerprintProPublicKey.handler({ isExisted })
  } catch (error) {
    console.warn("getFingerprintProPublicKey failed", error)
    return undefined
  }
}

// One agent per public key: the two flows use different keys and endpoints,
// and sharing an agent would silently send one flow's request with the other
// flow's key.
const agentsByApiKey = new Map<string, Agent>()

const initAgent = async (
  apiKey: string,
  endpoints: NonNullable<StartOptions["endpoints"]>
): Promise<Agent> => {
  const cachedAgent = agentsByApiKey.get(apiKey)
  if (cachedAgent) return cachedAgent

  const { start } = await import("@fingerprint/agent")
  const agent = start({ apiKey, region: resolveRegion(), endpoints })
  agentsByApiKey.set(apiKey, agent)

  return agent
}

const extractRawErrorMsg = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error

  try {
    return JSON.stringify(error)
  } catch {
    return UNKNOWN_AGENT_ERROR_MESSAGE
  }
}

// Trackers expect the agent's raw error message; the backend prepends the
// `[9W FPP ... License][env][linkedId][Alert]` prefix itself.
const formatTrackerErrorMsg = (error: unknown): string =>
  (extractRawErrorMsg(error) || UNKNOWN_AGENT_ERROR_MESSAGE).slice(
    0,
    TRACKER_ERROR_MSG_MAX_LENGTH
  )

interface FingerprintProIdentification {
  eventId?: string
  errorMsg?: string
}

interface FingerprintProIdentifyOptions {
  apiKey: string
  endpoints: NonNullable<StartOptions["endpoints"]>
  linkedId: string
  tag: Record<string, string>
}

const identifyVisitor = async ({
  apiKey,
  endpoints,
  linkedId,
  tag
}: FingerprintProIdentifyOptions): Promise<FingerprintProIdentification> => {
  try {
    const agent = await initAgent(apiKey, endpoints)
    const result = await agent.get({
      linkedId,
      tag,
      timeout: FINGERPRINT_PRO_GET_TIMEOUT_MS
    })

    return { eventId: result.event_id }
  } catch (error) {
    console.warn("Fingerprint agent failed", error)
    return { errorMsg: formatTrackerErrorMsg(error) }
  }
}

const runTrialFlow = async (trialPublicKey: string, linkedId: string) => {
  const { eventId, errorMsg } = await identifyVisitor({
    apiKey: trialPublicKey,
    endpoints: resolveTrialBackendProxyEndpoint(),
    linkedId,
    tag: TRIAL_TAG
  })

  await fingerprintProTracker.handler(
    eventId
      ? { eventId, publicKey: trialPublicKey }
      : { errorMsg: errorMsg ?? UNKNOWN_AGENT_ERROR_MESSAGE }
  )
}

const runEnterpriseFlow = async (
  enterprisePublicKey: string,
  linkedId: string,
  account: string | undefined
) => {
  const cloudflareEndpoint = resolveEnterpriseCloudflareProxyEndpoint()
  if (!cloudflareEndpoint) {
    console.warn("Fingerprint Enterprise skipped: no Cloudflare proxy configured")
    return
  }

  const { withoutDefault } = await import("@fingerprint/agent")
  const { eventId, errorMsg } = await identifyVisitor({
    apiKey: enterprisePublicKey,
    // Enterprise only ever goes through Cloudflare; never fall back to the
    // official *.fpjs.io hosts.
    endpoints: withoutDefault(cloudflareEndpoint),
    linkedId,
    tag: ENTERPRISE_TAG
  })

  if (!eventId) {
    await fingerprintProEnterpriseTracker.handler({
      errorMsg: errorMsg ?? UNKNOWN_AGENT_ERROR_MESSAGE
    })
    return
  }

  const isAccepted = await fingerprintProEnterpriseTracker.handler({ eventId })
  if (isAccepted) markEnterpriseRecorded(account)
}

// Wraps a flow so its failure is logged with context instead of rejecting:
// the two flows run side by side and must not affect each other.
const runIsolated = async (flowName: string, flow: () => Promise<void>) => {
  try {
    await flow()
  } catch (error) {
    console.warn(`Fingerprint ${flowName} flow failed`, error)
  }
}

const executeFingerprintProFlow = async (account: string | undefined) => {
  const publicKeyData = await resolvePublicKeyData(isEnterpriseRecorded(account))
  if (!publicKeyData) return

  const { siteId, userId, createdDate, trialPublicKey, enterprisePublicKey } =
    publicKeyData
  const linkedId = `${siteId}_${userId}_${createdDate}`

  await Promise.all([
    trialPublicKey
      ? runIsolated("Trial", () => runTrialFlow(trialPublicKey, linkedId))
      : undefined,
    enterprisePublicKey
      ? runIsolated("Enterprise", () =>
          runEnterpriseFlow(enterprisePublicKey, linkedId, account)
        )
      : undefined
  ])
}

// In-flight promise cache: the Fingerprint agent dedups parallel `.get()` calls
// so overlapping triggers would get the same event_id back and report it twice.
// Overlapping calls share the pending flow, so each tracker is called once per
// run. The cache is cleared once the flow settles, because logout does not
// reload the page: a later login (useFetchLogin) in the same page load must
// track again rather than reuse the previous user's settled result.
let inFlightFlow: ReturnType<typeof executeFingerprintProFlow> | undefined

// account is the logged-in account name, used only to key the Enterprise
// isExisted flag in localStorage.
export const runFingerprintProFlow = (account?: string) => {
  inFlightFlow ??= executeFingerprintProFlow(account).finally(() => {
    inFlightFlow = undefined
  })
  return inFlightFlow
}

export default runFingerprintProFlow
